import React from "react"

/**
 * EversportsWidget
 *
 * Renders a single Eversports booking widget using the NEW script-loader-based
 * embed (`https://widget-static.eversports.io/loader.js`). This replaces the
 * legacy per-page `<iframe src="https://www.eversports.de/widget/w/...">`
 * approach that lived in `src/components/Eversports/Eversports.tsx`.
 *
 * This component only renders the placeholder element the loader scans for:
 *
 *   <div data-eversports-widget-id="<id>" />
 *
 * Two pieces of global plumbing make it work (see those files for detail):
 *   - `gatsby-ssr.js`     injects the loader script once, and forces the widget
 *                         UI language to German by overriding navigator.language
 *                         (the loader reads `navigator.language` and has no
 *                         locale attribute/config).
 *   - `gatsby-browser.js` re-executes the loader on client-side route changes
 *                         (`onRouteUpdate`). The loader is a one-shot module
 *                         with no re-scan API or MutationObserver, so without
 *                         this the widget is blank after SPA navigation until a
 *                         full reload.
 *
 * Usage:
 *   import { EversportsWidget } from "../components/EversportsWidget"
 *   import { EVERSPORTS_IDS } from "../config/eversportsWidgets"
 *
 *   <EversportsWidget widgetId={EVERSPORTS_IDS.WORKSHOPS} label="Workshops" />
 *
 * Props:
 *   - widgetId:  the NEW Eversports widget ID (Eversports Manager → Settings → Widgets [NEW])
 *   - label:     optional human-readable hint; used only as an aria-label
 *   - className: optional wrapper class, kept for layout parity with the old
 *                <Eversports> embeds (the legacy component applied a className
 *                to its wrapper div). Not required by the loader.
 *
 * Migration context: see `src/config/eversportsWidgets.ts` for the OLD → NEW
 * ID registry.
 */

interface EversportsWidgetProps {
  widgetId: string
  label?: string
  className?: string
}

export const EversportsWidget: React.FC<EversportsWidgetProps> = ({
  widgetId,
  label,
  className,
}) => {
  return (
    <div className={className}>
      <div
        data-eversports-widget-id={widgetId}
        aria-label={label}
        className="w-full"
        // The new loader widget renders into this div, which has no intrinsic
        // width. The old <iframe> used width="100%"; replicate that so the
        // widget fills its container instead of collapsing to min-content
        // (e.g. inside a flex `justify-center` wrapper). Without this the
        // calendar/header columns get crushed into a narrow column.
        style={{ width: "100%" }}
      />
    </div>
  )
}
